import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { SiGoogle } from "react-icons/si";
import {
  HiOutlineMail,
  HiOutlineLockClosed,
  HiOutlineAcademicCap,
} from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";
import { signInWithPopup } from "firebase/auth";
import { auth, provider } from "./firebaseConfig";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ThreeDots } from "react-loader-spinner";
import { UserContext } from "../Context/usercontext";
import SelectCollege from "./SelectCollege";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { setUser } = useContext(UserContext);

  const [openSelectCollege, setOpenSelectCollege] = useState(false)

  const navigate = useNavigate();


  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/apis/userAuth/login`,
        { email, password },
      );

      if (response.status === 401) {
        toast.error(response.data.message);
      }

      if (response.status === 200) {
        Cookies.set("userToken", response.data.data.token, {
          expires: 1,
          secure: true,
          sameSite: "Strict",
        });

        setUser({
          ...response.data.data.user,
          token: response.data.data.token,
        });

        toast.success("Login successful! Redirecting...", {
          position: "top-right",
          autoClose: 3000,
        });

        setTimeout(() => navigate("/"), 3000);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error.response?.data?.error ||
          "Login failed. Try again!",
        {
          position: "top-right",
          autoClose: 3000,
        },
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      const result = await signInWithPopup(auth, provider);

      const user = result.user;
      const idToken = await user.getIdToken();

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/apis/userAuth/googleAuth`,
        { token: idToken },
      );

      if (response.data.success) {
        Cookies.set("userToken", response.data.data.token, {
          expires: 1,
          secure: true,
          sameSite: "Strict",
        });

        setUser({
          ...response.data.data.user,
          token: response.data.data.token,
        });

        toast.success("Google login successful!", {
          position: "top-right",
          autoClose: 3000,
        });

        if(!response.data.data.user?.college){
          setOpenSelectCollege(true);
        }else{
          setTimeout(() => navigate("/"), 3000);
        }
      }
    } catch (error) {
      toast.error("Google login failed. Try again!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
      {/* Animated background elements - Light Gray */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-60"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse",
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-60"
          animate={{
            x: [0, -50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            repeatType: "reverse",
          }}
        />
      </div>

      {/* Loading Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center"
            >
              <ThreeDots
                height="60"
                width="60"
                color="#6b7280"
                ariaLabel="loading"
              />
              <p className="text-gray-700 font-semibold mt-4">Logging in...</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content - Keeping original structure but enhancing visuals */}
      <div className="flex flex-col md:flex-row min-h-screen relative z-10">
        {/* Left Side - Image Section with Overlay */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="w-full md:w-1/3 h-64 md:h-screen relative overflow-hidden group"
        >
          <div className="w-full h-full bg-gradient-to-br from-gray-600 via-gray-700 to-gray-800 relative">
            {/* Animated overlay */}
            <motion.div
              className="absolute inset-0 bg-black opacity-20"
              animate={{
                opacity: [0.2, 0.3, 0.2],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                repeatType: "reverse",
              }}
            />

            {/* Image with subtle zoom effect */}
            <motion.img
              src="assets/bg.png"
              alt="Login Banner"
              className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-700"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.6 }}
            />

            {/* Floating alumni icon */}
            <motion.div
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white"
              animate={{
                y: [0, -10, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                repeatType: "reverse",
              }}
            >
              <HiOutlineAcademicCap className="w-20 h-20 opacity-20" />
            </motion.div>

            {/* Gradient overlay for text */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-8">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-white text-lg font-semibold"
              >
                Welcome back, Alumini!
              </motion.p>
            </div>
          </div>
        </motion.div>

        {/* Right Side - Login Form */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="w-full md:w-2/3 flex items-center justify-center p-6 md:p-12 bg-white/80 backdrop-blur-sm"
        >
          <div className="w-full max-w-md">
            {/* Header with animation */}
            <motion.div variants={itemVariants} className="mb-8">
              <motion.h2 className="text-4xl font-bold text-gray-700 mb-3">
                Welcome Back
              </motion.h2>
              <motion.p
                variants={itemVariants}
                className="text-gray-600 text-lg"
              >
                Log in to your alumni account and connect with your community
              </motion.p>
            </motion.div>

            <form onSubmit={handleLogin} className="space-y-5">
              {/* Email Field with Icon */}
              <motion.div variants={itemVariants} className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <HiOutlineMail className="h-5 w-5 text-gray-400" />
                  </div>
                  <motion.input
                    whileFocus={{ scale: 1.01 }}
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
                    required
                  />
                </div>
              </motion.div>

              {/* Password Field with Icon */}
              <motion.div variants={itemVariants} className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <HiOutlineLockClosed className="h-5 w-5 text-gray-400" />
                  </div>
                  <motion.input
                    whileFocus={{ scale: 1.01 }}
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
                    required
                  />
                </div>
              </motion.div>

              {/* Submit Button with Animation */}
              <motion.div variants={itemVariants}>
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gray-700 text-white text-lg font-semibold py-3 rounded-xl hover:bg-gray-800 transition-all duration-300 shadow-md"
                >
                  Log In
                </motion.button>
              </motion.div>

              {/* Divider */}
              <motion.div variants={itemVariants} className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <motion.span className="px-4 bg-white text-gray-500">
                    Or continue with
                  </motion.span>
                </div>
              </motion.div>

              {/* Google Button with Animation */}
              <motion.div variants={itemVariants}>
                <motion.button
                  type="button"
                  onClick={handleGoogleLogin}
                  whileHover={{ scale: 1.02, backgroundColor: "#f9fafb" }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full border-2 border-gray-200 py-3 rounded-xl flex items-center justify-center space-x-3 bg-white hover:border-gray-300 hover:shadow-md transition-all duration-300 group"
                >
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    <SiGoogle className="w-5 h-5 text-red-500" />
                  </motion.div>
                  <span className="text-gray-700 font-semibold group-hover:text-gray-900 transition-colors duration-300">
                    Google
                  </span>
                </motion.button>
              </motion.div>

              {/* Sign Up Link */}
              <motion.p
                variants={itemVariants}
                className="text-center text-gray-600 mt-6"
              >
                Don't have an account?{" "}
                <motion.span
                  onClick={() => navigate("/signup")}
                  className="font-semibold text-gray-700 cursor-pointer inline-block hover:text-gray-900"
                  whileHover={{ scale: 1.05, x: 2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Create account
                </motion.span>
              </motion.p>
            </form>
          </div>
        </motion.div>
      </div>

      {
        openSelectCollege &&
        <SelectCollege/>
      }
    </div>
  );
};

export default Login;
